import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import type { Permission } from "@shared/permissions";
import { useAuth } from "./AuthProvider";
import { FullPageSpinner } from "@/components/ui/Spinner";

interface Props {
  children: ReactNode;
  /** Every listed permission is required. Omit for "any signed-in user". */
  permissions?: Permission[];
  /** Only the master admin account may pass. */
  masterOnly?: boolean;
}

/**
 * Client-side gating is a UX affordance, not a security boundary — the
 * Worker re-checks every permission on every request. This just avoids
 * rendering a page the API would refuse to fill.
 */
export function ProtectedRoute({ children, permissions, masterOnly }: Props) {
  const { user, loading, can } = useAuth();
  const location = useLocation();

  if (loading) return <FullPageSpinner />;

  if (!user) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirectTo=${encodeURIComponent(next)}`} replace />;
  }

  if (masterOnly && !user.isMaster) return <Navigate to="/403" replace />;
  if (permissions?.length && !can(permissions)) return <Navigate to="/403" replace />;

  return <>{children}</>;
}
